import { useCallback, useEffect, useState } from 'react';
import { getUnreadMessageSummary } from '../services/messagesApi';
import { subscribeRealtimeNotifications } from '../utils/realtimeNotifications';

function emitUnreadRefresh() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('yubu-messages-unread-refresh'));
}

export function notifyMessageUnreadRefresh() {
  emitUnreadRefresh();
}

export function useMessageUnreadCount({ enabled = true, shopId = null } = {}) {
  const [unreadCount, setUnreadCount] = useState(0);

  const loadUnreadCount = useCallback(async () => {
    if (!enabled) {
      setUnreadCount(0);
      return 0;
    }

    try {
      const data = await getUnreadMessageSummary({ shopId });
      const count = Number(data?.unread_count || 0);
      setUnreadCount(count);
      return count;
    } catch {
      setUnreadCount(0);
      return 0;
    }
  }, [enabled, shopId]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      loadUnreadCount().catch(() => null);
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [loadUnreadCount]);

  useEffect(() => {
    if (!enabled) return undefined;

    const unsubscribeRealtime = subscribeRealtimeNotifications((event) => {
      const payload = event?.detail;
      if (payload?.event !== 'message_thread_updated') return;
      loadUnreadCount().catch(() => null);
    });

    const refreshHandler = () => {
      loadUnreadCount().catch(() => null);
    };

    window.addEventListener('yubu-messages-unread-refresh', refreshHandler);

    return () => {
      unsubscribeRealtime();
      window.removeEventListener('yubu-messages-unread-refresh', refreshHandler);
    };
  }, [enabled, loadUnreadCount]);

  return { unreadCount, refreshUnreadCount: loadUnreadCount };
}
